<template>
  <div class="feed-container">
    <!-- Feed Posts -->
    <div class="feed-posts">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <Loader2 class="loading-spinner" />
        <p class="loading-text">
          {{ t('feed.loadingPosts') }}
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="posts.length === 0" class="empty-state">
        <MessageSquare class="empty-icon" />
        <p class="empty-text">
          {{ t('feed.noPosts') }}
        </p>
      </div>

      <!-- Posts List -->
      <div v-else class="posts-list">
        <div
          v-for="post in posts"
          :key="post.id"
          class="post-row"
          :class="{ 'exploration-post': post.post_type === 'exploration' }"
        >
          <RouterLink :to="{ name: 'user-profile', params: { id: post.user_id } }">
            <img
              :src="getAvatarImage(post.profile?.thumbnail_url)"
              :alt="post.profile?.name"
              class="post-avatar"
            >
          </RouterLink>
          <div class="post-body">
            <div class="post-header-row">
              <RouterLink
                :to="{ name: 'user-profile', params: { id: post.user_id } }"
                class="post-author"
              >{{ post.profile?.name || 'Anonymous' }}</RouterLink>
              <span v-if="post.post_type === 'exploration'" class="exploration-badge">🧭</span>
              <time class="post-time">{{ formatTime(post.created_at) }}</time>
              <button
                v-if="isOwnPost(post)"
                class="delete-button"
                title="Delete post"
                @click="confirmDelete(post.id)"
              >
                <Trash2 class="delete-icon" />
              </button>
            </div>
            <div class="post-text">{{ post.content }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Visual Separator -->
    <div class="feed-separator">
      <div class="separator-line" />
      <span class="separator-text">{{ t('feed.shareThoughts') }}</span>
      <div class="separator-line" />
    </div>

    <!-- Post Composer -->
    <Card v-if="isAuthenticated" class="composer-card">
      <Textarea
        v-model="newPost"
        :placeholder="t('feed.postPlaceholder')"
        :maxlength="280"
        rows="4"
        variant="filled"
        @keydown.enter.meta.prevent="submitPost"
        @keydown.enter.ctrl.prevent="submitPost"
      />

      <div class="composer-footer">
        <span class="character-count" :class="{ 'over-limit': newPost.length > 250 }">
          {{ newPost.length }}/280
        </span>
        <Button
          :disabled="!newPost.trim()"
          :loading="posting"
          variant="primary"
          size="sm"
          form
          @click="submitPost"
        >
          {{ posting ? t('feed.posting') : t('feed.post') }}
        </Button>
      </div>
    </Card>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        class="w-full max-w-sm rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-xl"
      >
        <div class="text-center">
          <Trash2 class="mx-auto mb-4 h-12 w-12 text-error" />
          <h3 class="mb-2 font-headline text-title-lg text-on-surface">
            {{ t('feed.deletePost') }}
          </h3>
          <p class="mb-6 font-body text-body-md text-on-surface-variant">
            {{ t('feed.deleteConfirmation') }}
          </p>
          <div class="flex gap-3">
            <Button variant="outline" class="flex-1" @click="showDeleteConfirmation = false">
              {{ t('feed.cancel') }}
            </Button>
            <Button
              variant="danger"
              class="flex-1"
              :loading="deleting"
              form
              @click="confirmDeletePost"
            >
              <Loader2 v-if="deleting" class="h-4 w-4 mr-2 animate-spin" />
              {{ deleting ? t('feed.deleting') : t('feed.delete') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/auth'
import { getAvatarImage } from '../lib/imageUtils'
import { Loader2, MessageSquare, Trash2 } from 'lucide-vue-next'
import Card from './ui/Card.vue'
import Button from './ui/Button.vue'
import Textarea from './ui/Textarea.vue'

const { t } = useI18n()

const authStore = useAuthStore()

const posts = ref([])
const newPost = ref('')
const loading = ref(true)
const posting = ref(false)
const hasMore = ref(true)
const showDeleteConfirmation = ref(false)
const postToDelete = ref(null)
const deleting = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)

onMounted(async () => {
  await loadPosts()
})

async function loadPosts() {
  loading.value = true
  try {
    // Calculate 4 hours ago
    const fourHoursAgo = new Date()
    fourHoursAgo.setHours(fourHoursAgo.getHours() - 4)

    const { data, error } = await supabase
      .from('feed_posts')
      .select(
        `
        *,
        profile:profiles(name, thumbnail_url)
      `
      )
      .gte('created_at', fourHoursAgo.toISOString())
      .order('created_at', { ascending: false })

    if (error) throw error

    posts.value = data
    hasMore.value = false // No pagination needed
  } catch (error) {
    console.error('Error loading posts:', error)
  } finally {
    loading.value = false
  }
}

async function submitPost() {
  if (!newPost.value.trim() || posting.value || !authStore.user) return

  posting.value = true
  try {
    const { data, error } = await supabase
      .from('feed_posts')
      .insert({
        user_id: authStore.user.id,
        content: newPost.value.trim()
      })
      .select(
        `
        *,
        profile:profiles(name, thumbnail_url)
      `
      )
      .single()

    if (error) throw error

    // Add new post to the beginning of the feed
    posts.value.unshift(data)
    newPost.value = ''
  } catch (error) {
    console.error('Error posting:', error)
    alert('Failed to post. Please try again.')
  } finally {
    posting.value = false
  }
}

function confirmDelete(postId) {
  postToDelete.value = postId
  showDeleteConfirmation.value = true
}

async function confirmDeletePost() {
  if (!postToDelete.value) return

  deleting.value = true
  try {
    const { error } = await supabase.from('feed_posts').delete().eq('id', postToDelete.value)

    if (error) throw error

    posts.value = posts.value.filter(post => post.id !== postToDelete.value)
    showDeleteConfirmation.value = false
    postToDelete.value = null
  } catch (error) {
    console.error('Error deleting post:', error)
    alert('Failed to delete post.')
  } finally {
    deleting.value = false
  }
}

function isOwnPost(post) {
  return authStore.user && post.user_id === authStore.user.id
}

function formatTime(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d`

  return date.toLocaleDateString()
}
</script>

<style scoped>
/* Feed Container */
.feed-container {
  @apply space-y-5;
}

/* Visual Separator */
.feed-separator {
  @apply flex items-center gap-4 py-3;
}

.separator-line {
  @apply flex-1 h-px;
  background: linear-gradient(to right, transparent, var(--outline-variant), transparent);
}

.separator-text {
  @apply whitespace-nowrap px-2 font-label text-label-md;
  color: var(--on-surface-variant);
}

/* Feed Header */
.feed-header {
  @apply text-center space-y-1 mb-6;
}

.feed-title {
  @apply font-headline text-headline-md;
  color: var(--primary);
}

.feed-subtitle {
  @apply font-body text-body-sm;
  color: var(--on-surface-variant);
}

/* Post Composer */
.composer-card {
  padding: 1.5rem;
  background: var(--surface-container-low);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--outline-variant);
  border-radius: 16px;
  transition: all 0.3s ease;
}

.composer-card:hover {
  box-shadow: var(--shadow-md);
}

.composer-header {
  @apply flex items-center gap-3 mb-4;
}

.composer-avatar {
  @apply w-10 h-10 rounded-full object-cover;
}

.composer-name {
  @apply font-label text-label-md;
  color: var(--on-surface);
}

.composer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
}

.character-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--on-surface-variant);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: var(--surface-container-high);
  transition: all 0.2s ease;
}

.character-count.over-limit {
  color: var(--error);
  background: var(--error-container);
}

/* Feed Posts */
.feed-posts {
  @apply space-y-3;
}

/* Loading State */
.loading-state {
  @apply text-center py-12;
}

.loading-spinner {
  @apply mx-auto mb-3 h-8 w-8 animate-spin;
  color: var(--primary);
}

.loading-text {
  @apply font-body text-body-sm;
  color: var(--on-surface-variant);
}

/* Empty State */
.empty-state {
  @apply text-center py-12;
}

.empty-icon {
  @apply mx-auto mb-3 h-12 w-12;
  color: var(--outline);
}

.empty-text {
  @apply font-body text-body-sm;
  color: var(--on-surface-variant);
}

/* Posts List */
.posts-list {
  @apply space-y-3;
}

/* Post Row */
.post-row {
  @apply flex items-start gap-3 rounded-xl border p-3;
  background: var(--surface-container-lowest);
  border-color: var(--outline-variant);
  box-shadow: var(--shadow-xs);
}

.post-row.exploration-post {
  background: var(--secondary-fixed);
  border-color: var(--secondary-container);
}

.post-avatar {
  @apply mt-0.5 h-10 w-10 flex-shrink-0 rounded-full object-cover;
  @apply hover:opacity-80 transition-opacity;
}

.post-body {
  @apply flex-1 min-w-0;
}

/* Post Header Row — name · badge · time · delete all inline */
.post-header-row {
  @apply flex items-baseline gap-1.5 flex-wrap;
}

.post-author {
  @apply font-label text-label-md leading-none;
  color: var(--on-surface);
  text-decoration: none;
}

.post-author:hover {
  @apply underline;
}

.exploration-badge {
  @apply text-xs flex-shrink-0;
}

.post-time {
  @apply font-body text-body-xs leading-none;
  color: var(--outline);
}

.delete-button {
  @apply ml-auto flex-shrink-0 transition-colors;
  color: var(--outline);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 1;
}

.delete-button:hover {
  color: var(--error);
}

.delete-icon {
  @apply h-3.5 w-3.5;
}

/* Post Text */
.post-text {
  @apply mt-1 whitespace-pre-wrap break-words font-body text-body-sm leading-snug;
  color: var(--on-surface-variant);
}

/* Responsive Adjustments */
@media (max-width: 640px) {
  .composer-card {
    @apply p-3;
  }
}

/* High DPI / Retina Display Optimizations */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .post-avatar,
  .composer-avatar {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}

/* Dark mode support (if needed in future) */
@media (prefers-color-scheme: dark) {
  .feed-title {
    color: var(--primary);
  }

  .feed-subtitle {
    color: var(--on-surface-variant);
  }
}
</style>
